import { memo } from "react"

import TonIcon from "@/assets/icons/ton-icon.svg?react"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import { cn } from "@/shared/lib/utils"
import { useI18n } from "@/shared/i18n"
import type { PlatformGift } from "@/shared/api/types"

interface MarketplaceCardProps {
  gift: PlatformGift
  onBuy: () => void
  isBuying?: boolean
}

function getDisplayName(gift: PlatformGift): string {
  if (gift.collection && typeof gift.num === "number") {
    return `${gift.collection} #${gift.num}`
  }
  if (gift.collection) return gift.collection
  if (typeof gift.num === "number") return `Gift #${gift.num}`
  return gift.slug
}

function MarketplaceCardBase({ gift, onBuy, isBuying = false }: MarketplaceCardProps) {
  const { t } = useI18n()
  const name = getDisplayName(gift)
  const price = formatTonFromNano(gift.purchase_price_nano_ton)

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <button
        type="button"
        onClick={onBuy}
        disabled={isBuying}
        aria-label={name}
        className="flex w-full flex-col items-start gap-1 text-left transition-transform active:scale-[0.98] focus:outline-none disabled:cursor-not-allowed"
      >
        <div className="relative w-full pb-2.5">
          <div
            className="h-28 w-full overflow-hidden rounded-2xl bg-secondary-main bg-cover bg-center"
            style={{ backgroundImage: `url(${gift.small_image_url})` }}
          />
          <div className="absolute bottom-0 left-1/2 flex h-5 -translate-x-1/2 items-center gap-0.5 rounded-full bg-white px-1.5">
            <span className="text-[14px] font-medium leading-4 tracking-[-0.5px] text-black-100">
              {price}
            </span>
            <TonIcon className="size-3.5 text-black-100" />
          </div>
        </div>
        <span className="line-clamp-1 w-full text-center text-[14px] font-medium leading-4 tracking-[-0.5px] text-white">
          {name}
        </span>
      </button>
      <button
        type="button"
        onClick={onBuy}
        disabled={isBuying}
        aria-label={`${t("marketplace.buy")} ${name}`}
        className={cn(
          "flex w-full items-center justify-center rounded-full px-2.5 py-1.5 text-[14px] font-semibold leading-4 tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.97]",
          isBuying ? "cursor-not-allowed bg-[#00FE74]/50" : "bg-[#00FE74]"
        )}
      >
        {isBuying ? t("marketplace.buying") : t("marketplace.buy")}
      </button>
    </div>
  )
}

export const MarketplaceCard = memo(MarketplaceCardBase)
